import { useState } from 'react';
import PrivacyPolicyModal from './PrivacyPolicyModal';

const footerLinks = {
  서비스: [
    { label: '석면 개요', href: '#overview' },
    { label: '석면조사·농도측정', href: '#inspection' },
    { label: '석면 해체·제거', href: '#removal' },
    { label: '해체·제거 감리', href: '#supervision' },
    { label: '석면건축물 안전관리', href: '#safety' },
  ],
  회사소개: [
    { label: '기업 소개', href: '#' },
    { label: '주요 실적', href: '#' },
    { label: '인허가 현황', href: '#' },
    { label: '채용 안내', href: '#' },
  ],
  고객지원: [
    { label: '무료 상담 신청', href: '#contact' },
    { label: '자료실', href: '#' },
    { label: '공지사항', href: '#' },
    { label: '개인정보처리방침', href: '#' },
  ],
};

const institutions = [
  {
    name: '환경부',
    abbr: '환경부',
    color: 'bg-green-700',
    href: 'https://www.me.go.kr',
    favicon: 'https://www.google.com/s2/favicons?domain=moel.go.kr&sz=64',
  },
  {
    name: '한국환경공단',
    abbr: '한국환경공단',
    color: 'bg-teal-700',
    href: 'https://www.keco.or.kr',
    favicon: 'https://www.google.com/s2/favicons?domain=keco.or.kr&sz=64',
  },
  {
    name: '고용노동부',
    abbr: '고용노동부',
    color: 'bg-sky-800',
    href: 'https://www.moel.go.kr',
    favicon: 'https://www.google.com/s2/favicons?domain=moel.go.kr&sz=64',
  },
  {
    name: '한국산업안전보건공단',
    abbr: '산업안전보건공단',
    color: 'bg-orange-700',
    href: 'https://www.kosha.or.kr',
    favicon: 'https://www.google.com/s2/favicons?domain=kosha.or.kr&sz=64',
  },
  {
    name: '석면관리종합정보망',
    abbr: '석면관리종합정보망',
    color: 'bg-green-800',
    href: 'https://asbestos.me.go.kr',
    favicon: 'https://www.google.com/s2/favicons?domain=asbestos.me.go.kr&sz=64',
  },
  {
    name: '한국석면건축물안전관리협회',
    abbr: '한국석면건축물안전관리협회',
    color: 'bg-stone-700',
    href: 'https://www.kacsma.or.kr/',
    favicon: 'https://static.readdy.ai/image/fd48a8db4c051796bc9448912b11d012/e792451b8ec086ba9ee6717b906cb500.png',
  },
  {
    name: '석면피해예방센터',
    abbr: '석면피해예방센터',
    color: 'bg-red-800',
    href: 'https://www.adpbc.or.kr',
    favicon: 'https://static.readdy.ai/image/fd48a8db4c051796bc9448912b11d012/09b1e7536bbf0e04072e7823f98eaf13.png',
  },
];

export default function FooterSection() {
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, label: string) => {
    if (label === '개인정보처리방침') {
      e.preventDefault();
      setIsPrivacyOpen(true);
      return;
    }
    if (href.startsWith('#') && href.length > 1) {
      e.preventDefault();
      document.getElementById(href.replace('#', ''))?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <PrivacyPolicyModal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />
      <footer className="bg-stone-100 border-t border-stone-200">

        {/* 관련 기관 — PC만 표시 */}
        <div className="border-b border-stone-200 bg-stone-50 hidden md:block">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <p className="text-xs font-bold text-stone-400 tracking-widest uppercase mb-5 text-center">관련 기관</p>
            <div className="grid grid-cols-7 gap-4">
              {institutions.map((inst) => (
                <a
                  key={inst.name}
                  href={inst.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  title={inst.name}
                  className="flex flex-col items-center gap-1.5 group cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl overflow-hidden transition-all duration-200 group-hover:scale-110 group-hover:opacity-80">
                    <img
                      src={inst.favicon}
                      alt={inst.name}
                      className="w-12 h-12 object-contain"
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        target.style.display = 'none';
                        if (target.parentElement) {
                          target.parentElement.innerHTML = `<span class="text-stone-500 text-[9px] font-bold text-center leading-tight px-1">${inst.abbr}</span>`;
                        }
                      }}
                    />
                  </div>
                  <span className="text-[11px] text-stone-500 group-hover:text-green-800 transition-colors duration-200 text-center leading-tight break-keep">
                    {inst.abbr}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* 기업 정보 + 링크 그리드 */}
        <div className="max-w-7xl mx-auto px-6 py-14">
          {/* PC: 5열 그리드 */}
          <div className="hidden md:grid lg:grid-cols-5 gap-10 mb-10">
            {/* 회사 소개 + 주소/연락처 */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-16 h-16 flex items-center justify-center overflow-hidden rounded-xl shrink-0">
                  <img
                    src="https://public.readdy.ai/ai/img_res/07fe367d-3ac5-47b1-8ba3-b62df756f6ce.png"
                    alt="회사 로고"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <div className="text-lg font-bold text-green-950">석면 전문기업</div>
                  <div className="text-sm text-stone-400">ASBESTOS PROFESSIONAL</div>
                </div>
              </div>
              <p className="text-sm text-stone-500 leading-relaxed mb-5 max-w-xs">
                석면 조사부터 해체·제거, 감리, 안전관리까지
                전 과정을 전문적으로 처리하는 석면 종합 솔루션 기업입니다.
              </p>
              <div className="flex flex-col gap-2 text-sm text-stone-500 leading-relaxed border-t border-stone-200 pt-4">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-map-pin-line text-green-800 text-base"></i>
                  </div>
                  <span>경기도 화성시 만세구 꽃내음1길 5-4, 510호</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-green-800 text-base"></i>
                  </div>
                  <a href="tel:031-8043-8611" className="hover:text-green-800 transition-colors cursor-pointer">031-8043-8611</a>
                  <span className="text-stone-300">|</span>
                  <span className="text-stone-400">Fax. 031-8043-9211</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-green-800 text-base"></i>
                  </div>
                  <a href="mailto:sodamenc0728@daum.net" className="hover:text-green-800 transition-colors cursor-pointer">sodamenc0728@daum.net</a>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-user-line text-green-800 text-base"></i>
                  </div>
                  <span>대표자 : 곽연숙</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-file-list-3-line text-green-800 text-base"></i>
                  </div>
                  <span>사업자등록번호 : 443-88-03390</span>
                </div>
              </div>
            </div>

            {/* 링크 컬럼들 */}
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h4 className="text-xs font-bold text-stone-400 tracking-widest uppercase mb-4">{category}</h4>
                <ul className="flex flex-col gap-2.5">
                  {links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        onClick={(e) => handleNavClick(e, link.href, link.label)}
                        className="text-sm text-stone-600 hover:text-green-800 transition-colors duration-200 cursor-pointer"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* 모바일: 로고 + 상호명 + 주소/연락처 + 개인정보처리방침만 */}
          <div className="md:hidden mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 flex items-center justify-center overflow-hidden rounded-xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/07fe367d-3ac5-47b1-8ba3-b62df756f6ce.png"
                  alt="소담이앤씨 로고"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <div className="text-base font-bold text-green-950">(주)소담이앤씨</div>
                <div className="text-xs text-stone-400">석면 전문기업 | ASBESTOS PROFESSIONAL</div>
              </div>
            </div>

            <div className="flex flex-col gap-2.5 text-sm text-stone-600 border-t border-stone-200 pt-4 mb-5">
              <div className="flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-map-pin-line text-green-800 text-sm"></i>
                </div>
                <span>경기도 화성시 만세구 꽃내음1길 5-4, 510호</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-phone-line text-green-800 text-sm"></i>
                </div>
                <a href="tel:031-8043-8611" className="text-green-800 font-medium cursor-pointer">031-8043-8611</a>
                <span className="text-stone-300">|</span>
                <span className="text-stone-400 text-xs">Fax. 031-8043-9211</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-green-800 text-sm"></i>
                </div>
                <a href="mailto:sodamenc0728@daum.net" className="text-stone-600 cursor-pointer">sodamenc0728@daum.net</a>
              </div>
            </div>

            <button
              onClick={() => setIsPrivacyOpen(true)}
              className="text-sm text-stone-500 underline underline-offset-2 cursor-pointer hover:text-green-800 transition-colors"
            >
              개인정보처리방침
            </button>
          </div>

          {/* 하단 카피라이트 */}
          <div className="border-t border-stone-200 pt-6 flex items-center justify-between">
            <div className="text-sm text-stone-500 font-semibold">(주)소담이앤씨</div>
            <div className="text-sm text-stone-400">© 2026 (주)소담이앤씨. All rights reserved.</div>
          </div>
        </div>
      </footer>
    </>
  );
}
